import React from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { BaseFormPage } from "../../components/common/BaseForm";
import { useFormCompletion, useFormSubmission } from "../../hooks";
import { UserFormContent } from "./components/UserFormLayout";
import { 
  userFormValidationSchema, 
  getInitialUserFormValues 
} from "./validation/userFormValidation";
import { UserFormValues } from "./types/userFormTypes";
import { 
  useCreateUserMutation
} from "../../api/usersApi";
import { useAppSelector } from "../../app/hooks";
import { UserRoles } from "../../constants/roles";

const UserCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [createUser] = useCreateUserMutation();

  const currentUserRole = useAppSelector((state) => state.auth.user?.role);
  const isAdminOrSuperAdmin =
    currentUserRole === UserRoles.ADMIN || currentUserRole === UserRoles.SUPER_ADMIN;
  const entityLabel = isAdminOrSuperAdmin ? "User" : "Driver";

  const formik = useFormik<UserFormValues>({
    initialValues: {
      ...getInitialUserFormValues(),
      role: isAdminOrSuperAdmin ? UserRoles.DISPATCHER : UserRoles.TRUCK_DRIVER,
    },
    validationSchema: userFormValidationSchema,
    enableReinitialize: false,
    onSubmit: async (values) => {
      // If invalid, mark fields as touched to show errors and block submit
      const errors = await formik.validateForm();
      if (Object.keys(errors).length > 0) {
        formik.setTouched(
          Object.keys(errors).reduce((acc: any, key) => {
            acc[key] = true;
            return acc;
          }, {}),
          true
        );
        return;
      }

      await handleSubmit(async () => {
        const createUserDto: any = {
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
          role: values.role,
          isEmployee: values.isEmployee,
          isActive: true, // Default value for new employees
          employmentStartDate: values.employmentStartDate || undefined,
          employmentEndDate: values.employmentEndDate === null ? null : (values.employmentEndDate || undefined),
          dateOfBirth: values.dateOfBirth || undefined,
          familyHoliday: values.familyHoliday || undefined,
          worksWeekends: values.worksWeekends,
          // Send flat properties instead of nested objects
          street: values.isEmployee ? (values.address.street || undefined) : undefined,
          city: values.isEmployee ? (values.address.city || undefined) : undefined,
          state: values.isEmployee ? values.address.state : undefined,
          zipCode: values.isEmployee ? (values.address.zipCode || undefined) : undefined,
          country: values.isEmployee ? (values.address.country || undefined) : undefined,
          phoneNumber: values.isEmployee ? (values.phoneNumber || undefined) : undefined,
          emergencyContactName: values.isEmployee ? (values.emergencyContact.name || undefined) : undefined,
          emergencyContactPhone: values.isEmployee ? values.emergencyContact.phone : undefined,
          // For Dispatcher role, send null for driverInfo (backend validation expects this)
          // For TruckDriver role, send the driver info
          driverInfo: (values.isEmployee && values.role === "TruckDriver") ? {
            companyName: values.driverInfo.companyName || undefined,
            ein: values.driverInfo.ein || undefined,
            ssn: values.driverInfo.ssn || undefined,
          } : null,
        };

        console.log("Form values:", values);
        console.log("CreateUserDto being sent:", createUserDto);
        console.log("JSON stringified:", JSON.stringify(createUserDto, null, 2));
        console.log("employmentEndDate value:", values.employmentEndDate);
        console.log("driverInfo value:", values.driverInfo);
        console.log("isEmployee:", values.isEmployee, "role:", values.role);
        console.log("Role from formik.values:", formik.values.role);
        console.log("Role from values parameter:", values.role);

        return await createUser(createUserDto).unwrap();
      });
    },
  });

  const completionPercentage = useFormCompletion(formik.values);
  const { 
    isSubmitting, 
    submitError, 
    successMessage, 
    showValidationErrors, 
    handleSubmit 
  } = useFormSubmission({
    onSuccess: () => {
      setTimeout(() => {
        navigate("/users");
      }, 1500);
    },
    successMessage: `${entityLabel} created successfully!`,
  });

  const handleBack = () => {
    navigate("/users");
  };

  return (
    <BaseFormPage
      title={`Create New ${entityLabel}`}
      onBack={handleBack}
      formik={formik}
      isSubmitting={isSubmitting}
      successMessage={successMessage}
      errorMessage={submitError}
      completionPercentage={completionPercentage}
      showValidationErrors={showValidationErrors}
      submitButtonText={`Create ${entityLabel}`}
    >
      <UserFormContent
        mode="create"
        onSubmit={() => {}} // BaseFormPage handles submission
        isLoading={isSubmitting}
      />
    </BaseFormPage>
  );
};

export default UserCreatePage;